import type { StateCreator } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { Peer } from "@/react/types/Live/Peer";

interface Session {
  preJoinChecklist: {
    hasEnabledMicrophoneOnce: boolean;
    hasEnabledCameraOnce: boolean;
    hasOpenedSettingsModalOnce: boolean;
    markMicrophoneEnabledOnce: () => void;
    markCameraEnabledOnce: () => void;
    markOpenedSettingsModalOnce: () => void;
  };
  spectators: {
    shouldPoll: boolean;
    setShouldPoll: (shouldPoll: boolean) => void;
    isLoading: boolean;
    isFetching: boolean;
    loadFn: null | (() => Promise<void>);
    setLoadFn: (loadFn: () => Promise<void>) => void;
    load: () => void;
    hasNext: boolean;
    setHasNext: (hasNext: boolean) => void;
    loadMoreFn: null | (() => Promise<void>);
    setLoadMoreFn: (loadMoreFn: () => Promise<void>) => void;
    loadMore: () => void;
    total: number;
    setTotal: (total: number) => void;
    data: Array<Peer>;
    setData: (data: Array<Peer>) => void;
  };
}

export interface SessionSlice {
  session: Session;
}

export const createSessionSlice: StateCreator<
  SessionSlice,
  [],
  [["zustand/immer", never]],
  SessionSlice
> = immer((set, get) => ({
  session: {
    spectators: {
      shouldPoll: true,
      setShouldPoll: (shouldPoll: boolean) =>
        set(state => {
          state.session.spectators.shouldPoll = shouldPoll;
        }),
      isLoading: false,
      isFetching: false,
      loadFn: null,
      setLoadFn: (loadFn: () => Promise<void>) =>
        set(state => {
          state.session.spectators.loadFn = loadFn;
        }),
      load: () => {
        const loadFn =
          get().session.spectators.loadFn ?? (() => Promise.resolve());
        set(state => {
          state.session.spectators.isLoading = true;
        });
        void loadFn().finally(() => {
          set(state => {
            state.session.spectators.isLoading = false;
          });
        });
      },
      hasNext: false,
      setHasNext: (hasNext: boolean) =>
        set(state => {
          state.session.spectators.hasNext = hasNext;
        }),
      loadMoreFn: null,
      setLoadMoreFn: (loadMoreFn: () => Promise<void>) =>
        set(state => {
          state.session.spectators.loadMoreFn = loadMoreFn;
        }),
      loadMore: () => {
        const loadMoreFn =
          get().session.spectators.loadMoreFn ?? (() => Promise.resolve());
        set(state => {
          state.session.spectators.isFetching = true;
        });
        void loadMoreFn().finally(() => {
          set(state => {
            state.session.spectators.isFetching = false;
          });
        });
      },
      total: 0,
      setTotal: (total: number) =>
        set(state => {
          state.session.spectators.total = total;
        }),
      data: [],
      setData: (data: Array<Peer>) =>
        set(state => {
          state.session.spectators.data = data;
        }),
    },
    preJoinChecklist: {
      hasEnabledMicrophoneOnce: false,
      hasEnabledCameraOnce: false,
      hasOpenedSettingsModalOnce: false,
      markMicrophoneEnabledOnce: () => {
        set(state => {
          state.session.preJoinChecklist.hasEnabledMicrophoneOnce = true;
        });
      },
      markCameraEnabledOnce: () => {
        set(state => {
          state.session.preJoinChecklist.hasEnabledCameraOnce = true;
        });
      },
      markOpenedSettingsModalOnce: () => {
        set(state => {
          state.session.preJoinChecklist.hasOpenedSettingsModalOnce = true;
        });
      },
    },
  },
}));
